import { SuperSearchPageResult, SuperSearchResult } from 'api';
import { COMPANY_ACTION_QUERY_PARAM } from 'common/constants/pageActions';
import { WATERFALL_QUERY_PARAM_VIEW } from 'pages/Waterfall/constants';
import { shortDate } from 'utilities';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchPendingPage,
  SuperSearchSection,
} from './SuperSearch.types';

interface SuperSearchResultGroup {
  id: string;
  label: string;
  types: SuperSearchResult.type[];
}

interface LocalSuperSearchPage {
  key: string;
  label: string;
  path: string;
  requiredFeatureFlag?: string;
}

interface LocalSuperSearchFirm {
  id: number;
  name: string;
  path: string;
  slug: string;
}

interface LocalSuperSearchCompany {
  firmId: number;
  id: number;
  name: string;
  path: string;
  slug: string;
}

interface LocalSuperSearchMeasurementDate {
  date: string;
  id: number;
  name: string;
  slug: string;
}

const LOCAL_INITIAL_RESULT_LIMIT = 10;
const LOCAL_SEARCH_RESULT_LIMIT = 50;
const MIN_EXPANDED_LOCAL_SEARCH_LENGTH = 3;

const getLocalResultLimit = (query: string) =>
  query.trim().length >= MIN_EXPANDED_LOCAL_SEARCH_LENGTH ? LOCAL_SEARCH_RESULT_LIMIT : LOCAL_INITIAL_RESULT_LIMIT;

const RESULT_GROUPS: Record<string, SuperSearchResultGroup> = {
  measurementDates: {
    id: 'measurement-dates',
    label: 'Measurement dates',
    types: [SuperSearchResult.type.MEASUREMENT_DATE],
  },
  documents: { id: 'documents', label: 'Documents', types: [SuperSearchResult.type.DOCUMENT] },
  capTableVersions: {
    id: 'cap-table-versions',
    label: 'Cap Table versions',
    types: [SuperSearchResult.type.CAP_TABLE_VERSION],
  },
  financialsVersions: {
    id: 'financials-versions',
    label: 'Financials versions',
    types: [SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION],
  },
  valuationVersions: {
    id: 'valuation-versions',
    label: 'Valuation versions',
    types: [SuperSearchResult.type.VALUATION_VERSION],
  },
  valuationApproaches: {
    id: 'valuation-approaches',
    label: 'Valuation approaches',
    types: [SuperSearchResult.type.VALUATION_APPROACH],
  },
  waterfallViews: {
    id: 'waterfall-views',
    label: 'Waterfall views',
    types: [SuperSearchResult.type.WATERFALL_VIEW],
  },
  customViews: {
    id: 'custom-views',
    label: 'Custom views',
    types: [SuperSearchResult.type.CUSTOM_VIEW],
  },
  firms: { id: 'firms', label: 'Firms', types: [SuperSearchResult.type.FIRM] },
  companies: { id: 'companies', label: 'Companies', types: [SuperSearchResult.type.COMPANY] },
  firmPages: { id: 'firm-pages', label: 'Firm pages', types: [SuperSearchResult.type.FIRM_PAGE] },
  companyPages: { id: 'company-pages', label: 'Company pages', types: [SuperSearchResult.type.COMPANY_PAGE] },
};

const getResultGroups = (context: SuperSearchContext) => {
  if (context.company) {
    return [
      RESULT_GROUPS.measurementDates,
      RESULT_GROUPS.capTableVersions,
      RESULT_GROUPS.financialsVersions,
      RESULT_GROUPS.valuationVersions,
      RESULT_GROUPS.valuationApproaches,
      RESULT_GROUPS.waterfallViews,
      RESULT_GROUPS.customViews,
      RESULT_GROUPS.documents,
      RESULT_GROUPS.companyPages,
      RESULT_GROUPS.companies,
      RESULT_GROUPS.firmPages,
      RESULT_GROUPS.firms,
    ];
  }

  if (context.firm) {
    return [
      RESULT_GROUPS.measurementDates,
      RESULT_GROUPS.capTableVersions,
      RESULT_GROUPS.financialsVersions,
      RESULT_GROUPS.valuationVersions,
      RESULT_GROUPS.valuationApproaches,
      RESULT_GROUPS.waterfallViews,
      RESULT_GROUPS.customViews,
      RESULT_GROUPS.documents,
      RESULT_GROUPS.companies,
      RESULT_GROUPS.firmPages,
      RESULT_GROUPS.companyPages,
      RESULT_GROUPS.firms,
    ];
  }

  return [
    RESULT_GROUPS.firms,
    RESULT_GROUPS.companies,
    RESULT_GROUPS.measurementDates,
    RESULT_GROUPS.capTableVersions,
    RESULT_GROUPS.financialsVersions,
    RESULT_GROUPS.valuationVersions,
    RESULT_GROUPS.valuationApproaches,
    RESULT_GROUPS.waterfallViews,
    RESULT_GROUPS.customViews,
    RESULT_GROUPS.documents,
    RESULT_GROUPS.firmPages,
    RESULT_GROUPS.companyPages,
  ];
};

export const matchesSuperSearchQuery = (query: string, searchText: string) => {
  const normalizedSearchText = searchText.toLocaleLowerCase();
  return query
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(term => normalizedSearchText.includes(term));
};

export const getLocalSuperSearchEntityResults = ({
  companies,
  firms,
  query,
}: {
  companies: LocalSuperSearchCompany[];
  firms: LocalSuperSearchFirm[];
  query: string;
}): SuperSearchResult[] => {
  const resultLimit = getLocalResultLimit(query);
  const firmsById = new Map(firms.map(firm => [firm.id, firm]));
  const firmResults = firms
    .filter(firm => matchesSuperSearchQuery(query, `${firm.name} firm firms`))
    .slice(0, resultLimit)
    .map<SuperSearchResult>(firm => {
      const firmPayload = {
        id: firm.id,
        name: firm.name,
        slug: firm.slug,
      };

      return {
        firm: firmPayload,
        id: `firm:${firm.id}`,
        label: firm.name,
        path: firm.path,
        search_text: `${firm.name} firm firms`,
        subtitle: 'Firm',
        type: SuperSearchResult.type.FIRM,
      };
    });
  const companyResults = companies
    .filter(company => {
      const firm = firmsById.get(company.firmId);
      return Boolean(firm && matchesSuperSearchQuery(query, `${company.name} ${firm.name} company companies summary`));
    })
    .slice(0, resultLimit)
    .flatMap<SuperSearchResult>(company => {
      const firm = firmsById.get(company.firmId);
      if (!firm) {
        return [];
      }

      const firmPayload = {
        id: firm.id,
        name: firm.name,
        slug: firm.slug,
      };

      const result: SuperSearchResult = {
        company: {
          firm_id: company.firmId,
          id: company.id,
          name: company.name,
          slug: company.slug,
        },
        firm: firmPayload,
        id: `company:${company.id}`,
        label: company.name,
        path: company.path,
        search_text: `${company.name} ${firm.name} company companies summary`,
        subtitle: firm.name,
        type: SuperSearchResult.type.COMPANY,
      };

      return [result];
    });

  return [...firmResults, ...companyResults];
};

const getLocalMeasurementDateDetails = (measurementDate: LocalSuperSearchMeasurementDate) => {
  const [year, month] = measurementDate.date.split('-').map(Number);
  const quarter = Number.isInteger(month) && month >= 1 && month <= 12 ? Math.ceil(month / 3) : undefined;
  const formattedDate = shortDate(measurementDate.date);
  const label =
    quarter && Number.isInteger(year) && formattedDate
      ? `Q${quarter} ${year} — ${formattedDate}`
      : measurementDate.name;
  const searchAliases = [
    measurementDate.name,
    measurementDate.slug,
    measurementDate.date,
    formattedDate,
    quarter && Number.isInteger(year) ? `Q${quarter} ${year}` : undefined,
    year,
  ]
    .filter(Boolean)
    .join(' ');

  return { label, searchAliases };
};

export const getLocalSuperSearchMeasurementDateResults = ({
  context,
  measurementDates,
  path,
  query,
}: {
  context: SuperSearchContext;
  measurementDates: LocalSuperSearchMeasurementDate[];
  path: string;
  query: string;
}): SuperSearchResult[] => {
  if (!context.firm) {
    return [];
  }

  const { company, firm } = context;
  return [...measurementDates]
    .sort((left, right) => right.date.localeCompare(left.date))
    .flatMap(measurementDate => {
      const { label, searchAliases } = getLocalMeasurementDateDetails(measurementDate);

      if (!matchesSuperSearchQuery(query, searchAliases)) {
        return [];
      }

      const searchText = [company?.name, firm.name, searchAliases].filter(Boolean).join(' ');
      return [
        {
          company,
          firm,
          id: company
            ? `company:${company.id}:measurement-date:${measurementDate.date}`
            : `firm:${firm.id}:measurement-date:${measurementDate.date}`,
          label,
          measurement_date: measurementDate,
          path,
          search_text: searchText,
          subtitle: company ? `${company.name} / ${firm.name}` : firm.name,
          type: SuperSearchResult.type.MEASUREMENT_DATE,
        },
      ];
    })
    .slice(0, getLocalResultLimit(query));
};

export const getLocalSuperSearchPageResults = ({
  companyPages,
  context,
  firmPages,
  query,
}: {
  companyPages: LocalSuperSearchPage[];
  context: SuperSearchContext;
  firmPages: LocalSuperSearchPage[];
  query: string;
}): SuperSearchResult[] => {
  if (!context.firm) {
    return [];
  }

  const { company, firm } = context;
  const firmResults = firmPages.map(page => {
    const searchText = `${firm.name} ${page.label} ${page.key}`;

    return {
      firm,
      id: `firm:${firm.id}:page:${page.key}`,
      label: page.label,
      page: {
        key: page.key,
        label: page.label,
        scope: SuperSearchPageResult.scope.FIRM,
      },
      path: page.path,
      search_text: searchText,
      subtitle: firm.name,
      type: SuperSearchResult.type.FIRM_PAGE,
    };
  });
  const companyResults = companyPages.map(page => {
    const searchText = `${company?.name ?? ''} ${firm.name} ${page.label} ${page.key}`;

    return {
      company,
      firm,
      id: company ? `company:${company.id}:page:${page.key}` : `firm:${firm.id}:company-page:${page.key}`,
      label: page.label,
      page: {
        key: page.key,
        label: page.label,
        required_feature_flag: page.requiredFeatureFlag,
        scope: SuperSearchPageResult.scope.COMPANY,
      },
      path: page.path,
      search_text: searchText,
      subtitle: company ? `${company.name} / ${firm.name}` : firm.name,
      type: SuperSearchResult.type.COMPANY_PAGE,
    };
  });

  return [...firmResults, ...companyResults].filter(result => matchesSuperSearchQuery(query, result.search_text));
};

export const mergeSuperSearchResults = (...resultGroups: Array<SuperSearchResult[] | undefined>) => {
  const resultsById = new Map<string, SuperSearchResult>();

  resultGroups.forEach(results => {
    results?.forEach(result => {
      if (!resultsById.has(result.id)) {
        resultsById.set(result.id, result);
      }
    });
  });

  return Array.from(resultsById.values());
};

export const isSuperSearchSavedDestinationResult = (result: SuperSearchResult) =>
  result.type === SuperSearchResult.type.CAP_TABLE_VERSION ||
  result.type === SuperSearchResult.type.FINANCIAL_STATEMENT_VERSION ||
  result.type === SuperSearchResult.type.VALUATION_VERSION ||
  result.type === SuperSearchResult.type.VALUATION_APPROACH ||
  result.type === SuperSearchResult.type.WATERFALL_VIEW ||
  result.type === SuperSearchResult.type.CUSTOM_VIEW;

export const isSuperSearchResultInContext = (result: SuperSearchResult, context: SuperSearchContext) => {
  if (context.company) {
    if (result.type === SuperSearchResult.type.DOCUMENT) {
      return result.firm.id === context.firm?.id;
    }

    return result.firm.id === context.firm?.id && (!result.company || result.company.id === context.company.id);
  }

  if (context.firm) {
    return result.firm.id === context.firm.id;
  }

  return true;
};

const getDocumentContextRank = (
  item: SuperSearchDisplayItem,
  context: SuperSearchContext,
  activeMeasurementDate?: string
) => {
  const { result } = item;
  if (result.company?.id === context.company?.id) {
    return result.measurement_date?.date === activeMeasurementDate ? 0 : 1;
  }

  return result.firm.id === context.firm?.id ? 2 : 3;
};

const toResultItem = (
  result: SuperSearchResult,
  context: SuperSearchContext,
  pendingPage?: SuperSearchPendingPage
): SuperSearchDisplayItem => {
  const isPendingCompanyPage = Boolean(pendingPage && result.company);
  const isPendingFirmPage = pendingPage?.scope === SuperSearchPageResult.scope.FIRM;
  let { label, subtitle } = result;

  if (pendingPage) {
    if (isPendingCompanyPage) {
      label = result.company?.name ?? result.label;
      subtitle = `${result.firm.name} / Open ${pendingPage.label}`;
    } else if (isPendingFirmPage) {
      label = result.firm.name;
      subtitle = `Open ${pendingPage.label}`;
    }
  }

  return {
    companyAction: pendingPage?.companyAction,
    id: result.id,
    isOutsideContext: !isSuperSearchResultInContext(result, context),
    kind: 'result',
    label,
    page: result.page,
    result,
    subtitle,
    typeLabel: pendingPage?.companyAction ? 'Company action' : undefined,
  };
};

const getFirmPageCommands = (pageResults: SuperSearchResult[]): SuperSearchDisplayItem[] => {
  const pageResultsByKey = new Map<string, SuperSearchResult>();

  pageResults.forEach(result => {
    if (result.page && !pageResultsByKey.has(result.page.key)) {
      pageResultsByKey.set(result.page.key, result);
    }
  });

  return Array.from(pageResultsByKey.entries()).map(([pageKey, result]) => ({
    id: `firm-page-command:${pageKey}`,
    isOutsideContext: false,
    kind: 'firm-page-command',
    label: result.page?.label ?? result.label,
    page: result.page,
    result,
    subtitle: 'Choose a firm',
  }));
};

const getCompanyPageCommands = (
  pageResults: SuperSearchResult[],
  context: SuperSearchContext
): SuperSearchDisplayItem[] => {
  const pageResultsByKey = new Map<string, SuperSearchResult[]>();

  pageResults.forEach(result => {
    if (!result.page) {
      return;
    }

    const matchingResults = pageResultsByKey.get(result.page.key) ?? [];
    matchingResults.push(result);
    pageResultsByKey.set(result.page.key, matchingResults);
  });

  return Array.from(pageResultsByKey.entries()).map(([pageKey, matchingResults]) => {
    const contextualResult = matchingResults.find(result => isSuperSearchResultInContext(result, context));
    const result = contextualResult ?? matchingResults[0];

    return {
      id: `company-page-command:${pageKey}`,
      isOutsideContext: !contextualResult && Boolean(context.firm),
      kind: 'company-page-command',
      label: result.page?.label ?? result.label,
      page: result.page,
      result,
      subtitle: 'Choose a company',
    };
  });
};

const getDisplayItems = (
  results: SuperSearchResult[],
  context: SuperSearchContext,
  query: string,
  localResultIds?: Set<string>,
  pendingPage?: SuperSearchPendingPage
) => {
  const hasQuery = Boolean(query.trim());
  const scopedResults = context.firm ? results.filter(result => result.firm.id === context.firm?.id) : results;

  // Dynamic results require explicit search intent; only the bounded local
  // measurement-date preview is valid for an empty query.
  const contextResults = scopedResults.filter(result => {
    if (
      result.type !== SuperSearchResult.type.MEASUREMENT_DATE &&
      result.type !== SuperSearchResult.type.DOCUMENT &&
      !isSuperSearchSavedDestinationResult(result)
    ) {
      return true;
    }

    if (!hasQuery) {
      return Boolean(localResultIds?.has(result.id));
    }

    if (result.type === SuperSearchResult.type.DOCUMENT || isSuperSearchSavedDestinationResult(result)) {
      return true;
    }

    // Company scope needs company-specific dates. Firm/global scope uses one
    // deduplicated date per firm so the list does not repeat every company.
    return context.company ? Boolean(result.company) : !result.company;
  });

  if (pendingPage) {
    return contextResults
      .filter(result => {
        if (result.page?.key !== pendingPage.key) {
          return false;
        }

        return pendingPage.scope === SuperSearchPageResult.scope.FIRM
          ? result.type === SuperSearchResult.type.FIRM_PAGE
          : result.type === SuperSearchResult.type.COMPANY_PAGE && Boolean(result.company);
      })
      .map(result => toResultItem(result, context, pendingPage));
  }

  if (context.company) {
    return contextResults.map(result => toResultItem(result, context));
  }

  const firmPageResults = contextResults.filter(result => result.type === SuperSearchResult.type.FIRM_PAGE);
  const companyPageResults = contextResults.filter(result => result.type === SuperSearchResult.type.COMPANY_PAGE);
  const standardItems = contextResults
    .filter(
      result =>
        result.type !== SuperSearchResult.type.COMPANY_PAGE &&
        (context.firm || result.type !== SuperSearchResult.type.FIRM_PAGE)
    )
    .map(result => toResultItem(result, context));

  return [
    ...standardItems,
    ...(!context.firm ? getFirmPageCommands(firmPageResults) : []),
    ...getCompanyPageCommands(companyPageResults, context),
  ];
};

export const getSuperSearchSections = ({
  activeMeasurementDate,
  context,
  localPageTypes,
  localResultIds,
  pendingPage,
  query,
  results,
}: {
  activeMeasurementDate?: string;
  context: SuperSearchContext;
  localPageTypes?: Set<SuperSearchResult.type>;
  localResultIds?: Set<string>;
  pendingPage?: SuperSearchPendingPage;
  query: string;
  results: SuperSearchResult[];
}): SuperSearchSection[] => {
  const items = getDisplayItems(results, context, query, localResultIds, pendingPage);
  const contextualItems = items.filter(item => !item.isOutsideContext);
  const outsideItems = items.filter(item => item.isOutsideContext);
  const sections: SuperSearchSection[] = [];

  if (pendingPage) {
    if (contextualItems.length > 0) {
      const isFirmPage = pendingPage.scope === SuperSearchPageResult.scope.FIRM;
      sections.push({
        id: isFirmPage ? 'firms' : 'companies',
        items: contextualItems,
        label: isFirmPage ? 'Firms' : 'Companies',
      });
    }
  } else {
    const resultGroups = getResultGroups(context);
    const localPageGroups = resultGroups.filter(group => group.types.some(type => localPageTypes?.has(type)));
    const localResultGroups = resultGroups.filter(
      group =>
        !localPageGroups.includes(group) &&
        contextualItems.some(item => localResultIds?.has(item.result.id) && group.types.includes(item.result.type))
    );
    const orderedResultGroups = [
      ...localPageGroups,
      ...localResultGroups,
      ...resultGroups.filter(group => !localPageGroups.includes(group) && !localResultGroups.includes(group)),
    ];

    orderedResultGroups.forEach(group => {
      const groupItems = contextualItems.filter(item => group.types.includes(item.result.type));
      if (groupItems.length > 0) {
        const sectionItems =
          group.id === RESULT_GROUPS.documents.id
            ? [...groupItems].sort(
              (left, right) =>
                getDocumentContextRank(left, context, activeMeasurementDate) -
                  getDocumentContextRank(right, context, activeMeasurementDate)
            )
            : groupItems;
        sections.push({ id: group.id, items: sectionItems, label: group.label });
      }
    });
  }

  const hasInitialLocalOutsideItems = !query.trim() && outsideItems.some(item => localResultIds?.has(item.result.id));
  if ((query.trim() || pendingPage || hasInitialLocalOutsideItems) && outsideItems.length > 0) {
    sections.push({ id: 'other-results', items: outsideItems, label: 'Other results' });
  }

  return sections;
};

export const getGhostCompletion = (query: string, item?: SuperSearchDisplayItem) => {
  const trimmedQuery = query.trimStart();
  if (!trimmedQuery || !item?.label.toLocaleLowerCase().startsWith(trimmedQuery.toLocaleLowerCase())) {
    return '';
  }

  return item.label.slice(trimmedQuery.length);
};

export const appendSupportedSearchParams = (path: string, currentSearch: string, fallbackMeasurementDate?: string) => {
  const date = new URLSearchParams(currentSearch).get('date') ?? fallbackMeasurementDate;
  if (!date) {
    return path;
  }

  const searchSeparatorIndex = path.indexOf('?');
  const pathname = searchSeparatorIndex >= 0 ? path.slice(0, searchSeparatorIndex) : path;
  const targetSearch = searchSeparatorIndex >= 0 ? path.slice(searchSeparatorIndex + 1) : '';
  const searchParams = new URLSearchParams(targetSearch);
  searchParams.set('date', date);
  return `${pathname}?${searchParams.toString()}`;
};

export const getSuperSearchNavigationPath = ({
  currentPathname,
  currentSearch,
  fallbackMeasurementDate,
  initialContext,
  item,
}: {
  currentPathname: string;
  currentSearch: string;
  fallbackMeasurementDate?: string;
  initialContext: SuperSearchContext;
  item: SuperSearchDisplayItem;
}) => {
  const { result } = item;
  const measurementDate = result.measurement_date;
  const appendCompanyAction = (path: string) => {
    if (!item.companyAction) {
      return path;
    }

    const [pathname, targetSearch = ''] = path.split('?');
    const searchParams = new URLSearchParams(targetSearch);
    searchParams.set(COMPANY_ACTION_QUERY_PARAM, item.companyAction);
    return `${pathname}?${searchParams.toString()}`;
  };

  if (result.type === SuperSearchResult.type.DOCUMENT || isSuperSearchSavedDestinationResult(result)) {
    if (result.type === SuperSearchResult.type.WATERFALL_VIEW && result.waterfall_view) {
      const [pathname, targetSearch = ''] = result.path.split('?');
      const searchParams = new URLSearchParams(targetSearch);
      searchParams.set(WATERFALL_QUERY_PARAM_VIEW, String(result.waterfall_view.id));
      return `${pathname}?${searchParams.toString()}`;
    }

    return appendCompanyAction(result.path);
  }

  if (result.type !== SuperSearchResult.type.MEASUREMENT_DATE || !measurementDate) {
    const supportedCurrentSearch = item.companyAction ? '' : currentSearch;
    return appendCompanyAction(
      appendSupportedSearchParams(result.path, supportedCurrentSearch, fallbackMeasurementDate)
    );
  }

  const isCurrentCompany = Boolean(result.company && initialContext.company?.id === result.company.id);
  const isCurrentFirmPage = !result.company && !initialContext.company && initialContext.firm?.id === result.firm.id;
  const shouldPreserveDestination = isCurrentCompany || isCurrentFirmPage;
  const target = shouldPreserveDestination ? currentPathname : result.path;
  const [pathname, targetSearch = ''] = target.split('?');
  const searchParams = new URLSearchParams(shouldPreserveDestination ? currentSearch : targetSearch);
  searchParams.set('date', measurementDate.date);

  return appendCompanyAction(`${pathname}?${searchParams.toString()}`);
};
